import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Skeleton,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartTooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Cell,
  PieChart,
  Pie,
} from 'recharts';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InventoryIcon from '@mui/icons-material/Inventory';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1')
  .replace(/\/api\/v\d+$/, '');

function apiFetch(path: string, opts: RequestInit = {}) {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
  return fetch(`${API_BASE}/api/v1${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
}

interface SupplyRiskItem {
  itemId: string;
  itemCode: string;
  itemDescription: string;
  shortageFrequency: number;
  leadTimeCoverage: number;
  safetyStockRatio: number;
  riskScore: number;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  metrics: {
    shortageFreq: number;
    leadTimeCoverageValue: number;
    ssRatio: number;
  };
}

interface PlannedOrder {
  id: string;
  plannedOrderNumber: string;
  itemId: string;
  itemCode: string;
  itemDescription: string;
  orderType: string;
  quantity: number;
  unit: string;
  plannedStartDate: string;
  plannedEndDate: string;
  status: string;
  priority: number;
}

interface RiskMeta {
  totalItems: number;
  criticalCount: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
}

interface Snack { open: boolean; message: string; severity: 'success' | 'error' }
const SNACK_CLOSED: Snack = { open: false, message: '', severity: 'success' };

const RISK_COLORS: Record<string, string> = {
  CRITICAL: '#d32f2f',
  HIGH: '#f57c00',
  MEDIUM: '#f9a825',
  LOW: '#388e3c',
};

function riskChip(level: string) {
  const colors: Record<string, 'error' | 'warning' | 'info' | 'success'> = {
    CRITICAL: 'error',
    HIGH: 'warning',
    MEDIUM: 'info',
    LOW: 'success',
  };
  return <Chip label={level} color={colors[level] || 'default'} size="small" />;
}

function orderStatusColor(status: string): 'default' | 'primary' | 'warning' | 'success' | 'error' {
  const map: Record<string, 'default' | 'primary' | 'warning' | 'success' | 'error'> = {
    PLANNED: 'primary',
    CONFIRMED: 'success',
    RELEASED: 'warning',
    CANCELLED: 'error',
  };
  return map[status] || 'default';
}

// ─── Tab 0: Overview ──────────────────────────────────────────────────────────
const OverviewTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [risks, setRisks] = useState<SupplyRiskItem[]>([]);
  const [meta, setMeta] = useState<RiskMeta>({ totalItems: 0, criticalCount: 0, highCount: 0, mediumCount: 0, lowCount: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/planning/mrp/supply-risk');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setRisks(json.data || []);
      setMeta(json.metadata || { totalItems: 0, criticalCount: 0, highCount: 0, mediumCount: 0, lowCount: 0 });
    } catch {
      setRisks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const distributionData = [
    { name: 'Critical', value: meta.criticalCount, color: RISK_COLORS.CRITICAL },
    { name: 'High', value: meta.highCount, color: RISK_COLORS.HIGH },
    { name: 'Medium', value: meta.mediumCount, color: RISK_COLORS.MEDIUM },
    { name: 'Low', value: meta.lowCount, color: RISK_COLORS.LOW },
  ];

  const top5 = [...risks].sort((a, b) => b.riskScore - a.riskScore).slice(0, 5);

  const radarData = top5.map(item => ({
    item: item.itemCode,
    'Shortage Freq': Math.round(item.metrics.shortageFreq * 100),
    'Lead Time Risk': Math.round((1 - Math.min(item.metrics.leadTimeCoverageValue, 1)) * 100),
    'SS Risk': Math.round((1 - Math.min(item.metrics.ssRatio, 1)) * 100),
  }));

  const kpiCards = [
    { label: 'Total Items Tracked', value: meta.totalItems, color: '#1976d2', icon: <InventoryIcon /> },
    { label: 'Critical Risk Items', value: meta.criticalCount, color: RISK_COLORS.CRITICAL, icon: <WarningAmberIcon /> },
    { label: 'High Risk Items', value: meta.highCount, color: RISK_COLORS.HIGH, icon: <TrendingUpIcon /> },
    { label: 'Low Risk Items', value: meta.lowCount, color: RISK_COLORS.LOW, icon: <CheckCircleOutlineIcon /> },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Risk Overview</Typography>
        <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
      </Box>

      <Grid container spacing={2} mb={3}>
        {kpiCards.map(c => (
          <Grid item xs={12} sm={6} md={3} key={c.label}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" color="text.secondary">{c.label}</Typography>
                    {loading
                      ? <Skeleton variant="text" width={60} height={40} />
                      : <Typography variant="h4" fontWeight={700} color={c.color}>{c.value}</Typography>
                    }
                  </Box>
                  <Box sx={{ color: c.color, opacity: 0.7 }}>{c.icon}</Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>Risk Distribution</Typography>
            {loading
              ? <Skeleton variant="rectangular" height={220} />
              : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={distributionData} cx="50%" cy="50%" outerRadius={80}
                      dataKey="value" nameKey="name" label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}>
                      {distributionData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )
            }
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>Top 5 High-Risk Items — Risk Score</Typography>
            {loading
              ? <Skeleton variant="rectangular" height={220} />
              : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={top5} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 1]} tickFormatter={v => `${Math.round(v * 100)}%`} />
                    <YAxis type="category" dataKey="itemCode" width={90} />
                    <RechartTooltip formatter={(v: number) => [`${(v * 100).toFixed(1)}%`, 'Risk Score']} />
                    <Bar dataKey="riskScore" name="Risk Score" radius={[0, 4, 4, 0]}>
                      {top5.map((entry, i) => (
                        <Cell key={i} fill={RISK_COLORS[entry.riskLevel]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )
            }
          </Paper>
        </Grid>

        {radarData.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>Risk Factor Radar — Top 5 Items</Typography>
              {loading
                ? <Skeleton variant="rectangular" height={280} />
                : (
                  <ResponsiveContainer width="100%" height={280}>
                    <RadarChart data={radarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="item" />
                      <PolarRadiusAxis domain={[0, 100]} tickFormatter={v => `${v}%`} />
                      <Radar name="Shortage Freq" dataKey="Shortage Freq" stroke="#f44336" fill="#f44336" fillOpacity={0.2} />
                      <Radar name="Lead Time Risk" dataKey="Lead Time Risk" stroke="#ff9800" fill="#ff9800" fillOpacity={0.2} />
                      <Radar name="Safety Stock Risk" dataKey="SS Risk" stroke="#2196f3" fill="#2196f3" fillOpacity={0.2} />
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                )
              }
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

// ─── Tab 1: Risk Table ────────────────────────────────────────────────────────
const RiskTableTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [risks, setRisks] = useState<SupplyRiskItem[]>([]);
  const [filterLevel, setFilterLevel] = useState('');
  const [filterItem, setFilterItem] = useState('');
  const [snack, setSnack] = useState<Snack>(SNACK_CLOSED);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterLevel) params.set('threshold', filterLevel.toLowerCase());
      const res = await apiFetch(`/planning/mrp/supply-risk?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setRisks(json.data || []);
    } catch {
      setSnack({ open: true, message: 'Failed to load risk data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filterLevel]);

  useEffect(() => { load(); }, [load]);

  const displayed = risks.filter(r =>
    !filterItem || r.itemCode.toLowerCase().includes(filterItem.toLowerCase()) ||
    r.itemDescription.toLowerCase().includes(filterItem.toLowerCase())
  );

  return (
    <Box>
      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
        <TextField
          label="Search Item" size="small" value={filterItem}
          onChange={e => setFilterItem(e.target.value)} sx={{ width: 220 }}
        />
        <FormControl size="small" sx={{ width: 160 }}>
          <InputLabel>Min Risk Level</InputLabel>
          <Select value={filterLevel} label="Min Risk Level" onChange={e => setFilterLevel(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="low">Low+</MenuItem>
            <MenuItem value="medium">Medium+</MenuItem>
            <MenuItem value="high">High+</MenuItem>
            <MenuItem value="critical">Critical only</MenuItem>
          </Select>
        </FormControl>
        <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
      </Box>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>Item Code</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Risk Score</TableCell>
              <TableCell>Risk Level</TableCell>
              <TableCell align="right">Shortage Freq</TableCell>
              <TableCell align="right">Lead Time Coverage</TableCell>
              <TableCell align="right">SS Ratio</TableCell>
              <TableCell>Score Breakdown</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayed.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" py={3}>No risk data found</Typography>
                </TableCell>
              </TableRow>
            )}
            {displayed.map(r => (
              <TableRow key={r.itemId} hover>
                <TableCell><strong>{r.itemCode}</strong></TableCell>
                <TableCell>{r.itemDescription}</TableCell>
                <TableCell align="right">
                  <Typography fontWeight={700} color={RISK_COLORS[r.riskLevel]}>
                    {(r.riskScore * 100).toFixed(1)}%
                  </Typography>
                </TableCell>
                <TableCell>{riskChip(r.riskLevel)}</TableCell>
                <TableCell align="right">{(r.shortageFrequency * 100).toFixed(1)}%</TableCell>
                <TableCell align="right">
                  <Tooltip title={r.leadTimeCoverage >= 1 ? 'Adequate' : 'Insufficient'}>
                    <span style={{ color: r.leadTimeCoverage < 1 ? '#f57c00' : '#388e3c' }}>
                      {r.leadTimeCoverage.toFixed(2)}x
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <span style={{ color: r.safetyStockRatio < 1 ? '#d32f2f' : '#388e3c' }}>
                    {r.safetyStockRatio.toFixed(2)}x
                  </span>
                </TableCell>
                <TableCell sx={{ minWidth: 160 }}>
                  <Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption">Shortage</Typography>
                      <Typography variant="caption">{(r.metrics.shortageFreq * 40).toFixed(0)}pts</Typography>
                    </Box>
                    <LinearProgress variant="determinate"
                      value={r.metrics.shortageFreq * 100}
                      color="error" sx={{ mb: 0.5, height: 4 }} />
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption">Lead Time</Typography>
                      <Typography variant="caption">{((1 - Math.min(r.metrics.leadTimeCoverageValue, 1)) * 30).toFixed(0)}pts</Typography>
                    </Box>
                    <LinearProgress variant="determinate"
                      value={(1 - Math.min(r.metrics.leadTimeCoverageValue, 1)) * 100}
                      color="warning" sx={{ mb: 0.5, height: 4 }} />
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption">Safety Stock</Typography>
                      <Typography variant="caption">{((1 - Math.min(r.metrics.ssRatio, 1)) * 30).toFixed(0)}pts</Typography>
                    </Box>
                    <LinearProgress variant="determinate"
                      value={(1 - Math.min(r.metrics.ssRatio, 1)) * 100}
                      color="info" sx={{ height: 4 }} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(SNACK_CLOSED)}>
        <Alert severity={snack.severity} onClose={() => setSnack(SNACK_CLOSED)}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

// ─── Tab 2: Planned Orders ────────────────────────────────────────────────────
const PlannedOrdersTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<PlannedOrder[]>([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [snack, setSnack] = useState<Snack>(SNACK_CLOSED);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterType) params.set('orderType', filterType);
      const res = await apiFetch(`/planning/mrp/planned-orders?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setOrders(json.data || []);
    } catch {
      setSnack({ open: true, message: 'Failed to load planned orders', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (id: string, action: 'confirm' | 'cancel') => {
    setActionLoading(id);
    try {
      const res = await apiFetch(`/planning/mrp/planned-orders/${id}/${action}`, { method: 'PATCH' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSnack({ open: true, message: `Order ${action}ed successfully`, severity: 'success' });
      await load();
    } catch {
      setSnack({ open: true, message: `Failed to ${action} order`, severity: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Box>
      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
        <FormControl size="small" sx={{ width: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="PLANNED">Planned</MenuItem>
            <MenuItem value="CONFIRMED">Confirmed</MenuItem>
            <MenuItem value="RELEASED">Released</MenuItem>
            <MenuItem value="CANCELLED">Cancelled</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 160 }}>
          <InputLabel>Order Type</InputLabel>
          <Select value={filterType} label="Order Type" onChange={e => setFilterType(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="PURCHASE">Purchase</MenuItem>
            <MenuItem value="PRODUCTION">Production</MenuItem>
            <MenuItem value="TRANSFER">Transfer</MenuItem>
          </Select>
        </FormControl>
        <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
      </Box>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>Order #</TableCell>
              <TableCell>Item</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell>Planned Start</TableCell>
              <TableCell>Planned End</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="text.secondary" py={3}>No planned orders found</Typography>
                </TableCell>
              </TableRow>
            )}
            {orders.map(o => (
              <TableRow key={o.id} hover>
                <TableCell>{o.plannedOrderNumber}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{o.itemCode}</Typography>
                  <Typography variant="caption" color="text.secondary">{o.itemDescription}</Typography>
                </TableCell>
                <TableCell>{o.orderType}</TableCell>
                <TableCell align="right">{o.quantity} {o.unit}</TableCell>
                <TableCell>{o.plannedStartDate ? new Date(o.plannedStartDate).toLocaleDateString() : '—'}</TableCell>
                <TableCell>{o.plannedEndDate ? new Date(o.plannedEndDate).toLocaleDateString() : '—'}</TableCell>
                <TableCell>
                  <Chip label={`P${o.priority}`} size="small"
                    color={o.priority <= 2 ? 'error' : o.priority <= 3 ? 'warning' : 'default'} />
                </TableCell>
                <TableCell>
                  <Chip label={o.status} color={orderStatusColor(o.status)} size="small" />
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5}>
                    {o.status === 'PLANNED' && (
                      <Button size="small" variant="outlined" color="success"
                        disabled={actionLoading === o.id}
                        onClick={() => doAction(o.id, 'confirm')}>
                        Confirm
                      </Button>
                    )}
                    {(o.status === 'PLANNED' || o.status === 'CONFIRMED') && (
                      <Button size="small" variant="outlined" color="error"
                        disabled={actionLoading === o.id}
                        onClick={() => doAction(o.id, 'cancel')}>
                        Cancel
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(SNACK_CLOSED)}>
        <Alert severity={snack.severity} onClose={() => setSnack(SNACK_CLOSED)}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

// ─── Tab 3: Yield Analysis ────────────────────────────────────────────────────
const YieldAnalysisTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState<any>(null);
  const [snack, setSnack] = useState<Snack>(SNACK_CLOSED);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/planning/mrp/yield-analysis');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setAnalysis(json.data || null);
    } catch {
      setSnack({ open: true, message: 'Failed to load yield analysis', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = analysis
    ? [
        { label: 'On Target (≥95%)', key: 'onTarget', color: '#388e3c', items: analysis.onTarget?.items || [] },
        { label: 'At Risk (85–95%)', key: 'atRisk', color: '#f57c00', items: analysis.atRisk?.items || [] },
        { label: 'Poor Yield (<85%)', key: 'poorYield', color: '#d32f2f', items: analysis.poorYield?.items || [] },
      ]
    : [];

  const chartData = categories.map(c => ({
    name: c.label.split(' ')[0] + ' ' + c.label.split(' ')[1],
    count: c.items.length,
    fill: c.color,
  }));

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Yield Performance Analysis</Typography>
        <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
      </Box>

      {loading
        ? <Skeleton variant="rectangular" height={120} sx={{ mb: 2 }} />
        : analysis && (
          <Grid container spacing={2} mb={3}>
            {categories.map(c => (
              <Grid item xs={12} sm={4} key={c.key}>
                <Card sx={{ borderLeft: `4px solid ${c.color}` }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">{c.label}</Typography>
                    <Typography variant="h4" fontWeight={700} color={c.color}>{c.items.length}</Typography>
                    <Typography variant="caption" color="text.secondary">items</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )
      }

      {!loading && analysis && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>Yield Category Distribution</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <RechartTooltip />
                  <Bar dataKey="count" name="Items" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            {categories.map(c => c.items.length > 0 && (
              <Paper key={c.key} sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} color={c.color} mb={1}>
                  {c.label} ({c.items.length} items)
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item Code</TableCell>
                        <TableCell align="right">Actual Yield</TableCell>
                        <TableCell align="right">Good Output</TableCell>
                        <TableCell align="right">Total Input</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {c.items.slice(0, 5).map((item: any) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.itemCode}</TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={600} color={c.color}>
                              {item.actualYield != null ? `${item.actualYield.toFixed(1)}%` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{item.actualGoodOutput ?? '—'}</TableCell>
                          <TableCell align="right">{item.totalInput ?? '—'}</TableCell>
                        </TableRow>
                      ))}
                      {c.items.length > 5 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="caption" color="text.secondary">
                              +{c.items.length - 5} more items
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))}
          </Grid>
        </Grid>
      )}

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(SNACK_CLOSED)}>
        <Alert severity={snack.severity} onClose={() => setSnack(SNACK_CLOSED)}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

// ─── Main Shell ───────────────────────────────────────────────────────────────
const SupplyChainRisk: React.FC = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Typography variant="h4" mb={3}>Supply Chain Risk</Typography>
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Risk Overview" />
          <Tab label="Item Risk Table" />
          <Tab label="Planned Orders" />
          <Tab label="Yield Analysis" />
        </Tabs>
      </Paper>
      {tab === 0 && <OverviewTab />}
      {tab === 1 && <RiskTableTab />}
      {tab === 2 && <PlannedOrdersTab />}
      {tab === 3 && <YieldAnalysisTab />}
    </Box>
  );
};

export default SupplyChainRisk;
